import {
  type LineNameMap,
  type SeriesEntry,
  type UPlotSeriesLayout,
} from './getSeriesEntries';

/** The X and Y values for one line or one edge of a min/max band. */
export type FacetData = [number[], (number | null)[]];
export type FacetedData = [[], ...FacetData[]];
type PreparedCoordinates = {
  xValues: number[];
  yValues: (number | null)[];
  upperYValues: (number | null)[] | undefined;
};
type CoordinateMode = 'primary' | 'band';

/**
 * Prepare each line's own X and Y values for drawing, plus both edges of each
 * min/max band. Lines keep their own points instead of adding empty Y values
 * for X values that occur only in other lines.
 */
export function getFacetedData(
  {seriesEntries: entries, lineNameMap}: UPlotSeriesLayout,
  xLogScale = false
): FacetedData {
  // uPlot reserves the first position for shared X values. These lines each have
  // their own X values, so leave it empty. The React wrapper requires an array here.
  const data: FacetedData = [[]];
  // A min/max band stores both edges in the same source points. Prepare those
  // points once, then reuse their X values when adding the second edge.
  const bandCoordinates = new Map<SeriesEntry['line'], PreparedCoordinates>();
  for (let seriesIdx = 1; seriesIdx < entries.length; seriesIdx++) {
    const entry = entries[seriesIdx];
    if (entry == null) {
      continue;
    }
    if (entry.type === 'primary-series') {
      const bandXs = getEligibleBandXValues(
        data,
        entry,
        seriesIdx,
        lineNameMap
      );
      const shared =
        bandXs != null
          ? tryBuildPrimaryWithSharedX(entry.line, xLogScale, bandXs)
          : undefined;
      const coordinates =
        shared ?? prepareCoordinates(entry.line, xLogScale, 'primary');
      data.push([coordinates.xValues, coordinates.yValues]);
      continue;
    }
    let coordinates = bandCoordinates.get(entry.line);
    if (coordinates == null) {
      coordinates = prepareCoordinates(entry.line, xLogScale, 'band');
      bandCoordinates.set(entry.line, coordinates);
    }
    // Band points provide minimum Y values in `y` and maximum Y values in `y0`.
    const yValues =
      entry.type === 'band-min'
        ? coordinates.yValues
        : coordinates.upperYValues!;
    data.push([coordinates.xValues, yValues]);
  }
  return data;
}

/**
 * Find the X values already prepared for this run and metric's min/max band.
 * The main line can reuse them if its points match. Band edges are prepared
 * before main lines, so these X values are already available.
 */
function getEligibleBandXValues(
  data: FacetedData,
  entry: SeriesEntry,
  seriesIdx: number,
  lineNameMap: LineNameMap
): number[] | undefined {
  const {name, metricName} = entry.line;
  if (name == null || metricName == null) {
    return undefined;
  }
  const indices = lineNameMap.get(name)?.get(metricName);
  // Only the main line can share its band's X values. Show Original lines
  // have separate points and must not borrow the main line's band.
  if (
    indices?.primarySeriesIndex !== seriesIdx ||
    indices.bandMinSeriesIndex == null
  ) {
    return undefined;
  }
  return (data[indices.bandMinSeriesIndex] as FacetData)[0];
}

/**
 * Prepare a main line's Y values while reusing its band's X values.
 * Ignore X values that cannot appear on a logarithmic axis, and keep the last
 * Y value when consecutive points have the same X. If the remaining X values
 * do not match the band's exactly, return undefined so the line gets its own.
 */
function tryBuildPrimaryWithSharedX(
  line: SeriesEntry['line'],
  xLogScale: boolean,
  xs: number[]
): PreparedCoordinates | undefined {
  // oxlint-disable-next-line unicorn/no-new-array -- exact-size preallocation
  const ys: FacetData[1] = new Array(xs.length);
  let count = 0;
  for (const point of line.data) {
    const {x} = point;
    if (xLogScale && x <= 0) {
      continue;
    }
    if (count > 0 && x === xs[count - 1]) {
      ys[count - 1] = point.y;
    } else if (x === xs[count]) {
      ys[count++] = point.y;
    } else {
      return undefined;
    }
  }
  return count === xs.length
    ? {xValues: xs, yValues: ys, upperYValues: undefined}
    : undefined;
}

/**
 * Prepare X and Y values for a line, including maximum Y values for a band.
 * Use the points' existing order when possible; otherwise sort them by X.
 */
function prepareCoordinates(
  line: SeriesEntry['line'],
  xLogScale: boolean,
  mode: CoordinateMode
): PreparedCoordinates {
  return (
    tryBuildOrderedCoordinates(line, xLogScale, mode) ??
    buildSortedCoordinates(line, xLogScale, mode)
  );
}

/**
 * Prepare a line whose X values already run from smallest to largest.
 * Keep the last Y value for repeated X values and skip zero or negative X values
 * on a logarithmic axis. For a band, also collect the maximum Y values.
 * If a later X value is not equal to or greater than the previous one, return
 * undefined so the caller can sort the points instead.
 */
function tryBuildOrderedCoordinates(
  line: SeriesEntry['line'],
  xLogScale: boolean,
  mode: CoordinateMode
): PreparedCoordinates | undefined {
  const n = line.data.length;
  // oxlint-disable-next-line unicorn/no-new-array -- preallocate, then trim after dedup
  const xs: number[] = new Array(n);
  // oxlint-disable-next-line unicorn/no-new-array -- preallocate, then trim after dedup
  const yValues: FacetData[1] = new Array(n);
  const upperYValues: FacetData[1] | undefined =
    // oxlint-disable-next-line unicorn/no-new-array -- preallocate, then trim after dedup
    mode === 'band' ? new Array(n) : undefined;
  let count = 0;
  for (const point of line.data) {
    const {x} = point;
    if (xLogScale && x <= 0) {
      continue;
    }
    let index = count;
    if (index > 0) {
      const previousX = xs[index - 1];
      if (x === previousX) {
        index--;
      } else if (!(x > previousX)) {
        return undefined;
      }
    }
    xs[index] = x;
    yValues[index] = point.y;
    if (upperYValues != null) {
      upperYValues[index] = point.y0 ?? null;
    }
    count = index + 1;
  }
  xs.length = count;
  yValues.length = count;
  if (upperYValues != null) {
    upperYValues.length = count;
  }
  return {xValues: xs, yValues, upperYValues};
}

/**
 * Prepare a line whose points need sorting by X, for example after an X expression
 * changes their order. Keep the last source point for each X value and skip zero
 * or negative X values on a logarithmic axis. A band keeps both Y values from
 * that same point, so its minimum and maximum edges stay paired.
 */
function buildSortedCoordinates(
  line: SeriesEntry['line'],
  xLogScale: boolean,
  mode: CoordinateMode
): PreparedCoordinates {
  const points = new Map<number, SeriesEntry['line']['data'][number]>();
  for (const point of line.data) {
    if (xLogScale && point.x <= 0) {
      continue;
    }
    points.set(point.x, point);
  }
  const xs = Array.from(points.keys()).sort((a, b) => a - b);
  const yValues: FacetData[1] = [];
  const upperYValues: FacetData[1] | undefined =
    mode === 'band' ? [] : undefined;
  for (const x of xs) {
    const point = points.get(x)!;
    yValues.push(point.y);
    if (upperYValues != null) {
      upperYValues.push(point.y0 ?? null);
    }
  }
  return {xValues: xs, yValues, upperYValues};
}
